import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { apiClient } from '@api/client';
import { useAuthStore } from '@store/authStore';
import type { TeamsConnection } from '@store/authStore';
import { useNotificationStore } from '@store/notificationStore';

interface TeamsAuthorizationUrlResponse {
  authorization_url: string;
}

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    return error.response?.data?.detail || 'Teams request failed';
  }
  return 'Teams request failed';
};

export const useConnectTeams = () => {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({ returnPath, mode = 'admin' }: { returnPath: string; mode?: 'admin' | 'user' }) => {
      const { data } = await apiClient.get<TeamsAuthorizationUrlResponse>(
        `/auth/teams/url?return_path=${encodeURIComponent(returnPath)}&mode=${mode}`
      );
      return data;
    },
    onError: (error) => {
      addNotification({
        message: extractErrorMessage(error),
        type: 'error',
      });
    },
  });
};

export const useDisconnectTeams = () => {
  const queryClient = useQueryClient();
  const { currentUser, setCurrentUser } = useAuthStore();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<TeamsConnection>('/auth/teams/disconnect');
      return data;
    },
    onSuccess: (teamsConnection) => {
      if (currentUser) {
        setCurrentUser({ ...currentUser, teams_connection: teamsConnection });
      }
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      addNotification({
        message: 'Teams disconnected',
        type: 'success',
      });
    },
    onError: (error) => {
      addNotification({
        message: extractErrorMessage(error),
        type: 'error',
      });
    },
  });
};
